/**
 * What the on-device model needs from the browser, checked on the leader tab's main thread before the
 * engine is told it has a runtime (models.md §6.2). Every assumption fails into a plain sentence, never a
 * blank screen: the engine then runs without `onDeviceModels`, its pending rows wait with reason `device`,
 * and the Models screen can show `reason`.
 *
 * Needed: WebAssembly (with SIMD — the wasm build of onnxruntime-web is SIMD-only), module workers,
 * MessageChannel, WebCrypto's SHA-256. NOT needed (each has a fallback): OPFS (memory store for the session),
 * persistent storage (shown as "may be removed"), threads / SharedArrayBuffer (one thread), the Battery
 * Status API (power is 'normal'), `navigator.deviceMemory` (no warning).
 */

export interface OnDeviceSupport {
  supported: boolean;
  /** Why not, in words for the person; null when supported. */
  reason: string | null;
}

/**
 * A minimal module using SIMD (`i32.const 0; i8x16.splat; i8x16.popcnt` returning a v128): validates only
 * where WASM SIMD exists (the probe of the wasm-feature-detect project).
 */
const SIMD_PROBE = new Uint8Array([0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11]);

export function detectOnDeviceSupport(): OnDeviceSupport {
  const g = globalThis as { WebAssembly?: typeof WebAssembly; MessageChannel?: unknown; crypto?: Crypto };
  if (typeof g.WebAssembly !== 'object' || typeof g.WebAssembly.validate !== 'function') {
    return { supported: false, reason: 'This browser has WebAssembly turned off, which the on-device model needs. (Some privacy settings disable it.)' };
  }
  let simd = false;
  try {
    simd = g.WebAssembly.validate(SIMD_PROBE);
  } catch {
    simd = false;
  }
  if (!simd) return { supported: false, reason: 'This browser’s WebAssembly has no SIMD, which the on-device model needs. Update the browser.' };
  if (typeof g.MessageChannel !== 'function') return { supported: false, reason: 'This browser cannot connect workers (no MessageChannel).' };
  if (typeof g.crypto?.subtle?.digest !== 'function') return { supported: false, reason: 'This page is not a secure context, so the model files cannot be verified (no WebCrypto).' };
  // Module workers: the engine itself runs in one, so a tab that got this far has them. A model worker
  // that still fails to start (or dies) is reported by the host as 'model-worker-error' in words.
  if (typeof (globalThis as { Worker?: unknown }).Worker !== 'function') return { supported: false, reason: 'This browser cannot start workers, which the on-device model runs in.' };
  return { supported: true, reason: null };
}

/** `navigator.deviceMemory` in GB where the browser tells it (Chromium); null elsewhere (Firefox, Safari). */
export function deviceMemoryGb(): number | null {
  const m = (globalThis as { navigator?: { deviceMemory?: unknown } }).navigator?.deviceMemory;
  return typeof m === 'number' && m > 0 ? m : null;
}
